import axios, { AxiosError } from 'axios';

// 默认用相对路径，便于：
// - 本地开发：CRA proxy 转发 /api 到后端
// - 生产环境：由 Nginx 反代 /api 到后端
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// 请求拦截器 - 添加token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = config.headers ?? {};
      (config.headers as any).Authorization = `Bearer ${token}`;
      // 额外在查询参数中带上 token，规避某些代理链路吞掉 Authorization 头的问题
      config.params = config.params ?? {};
      (config.params as any).token = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器 - 只处理错误（不改写成功返回值，避免 TS 类型与运行时不一致）
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<any>) => {
    const data: any = error.response?.data;
    
    // 401错误处理：如果不是登录接口，则跳转到登录页
    if (error.response?.status === 401) {
      // 如果是登录接口（/auth/login），不要自动跳转，让错误被正常处理
      if (!error.config?.url?.includes('/auth/login')) {
        localStorage.removeItem('token');
        window.location.href = '/login';
        return Promise.reject({ error: '登录已过期，请重新登录' });
      }
    }
    
    // 统一错误结构，兼容后端 {msg: "..."} / {error: "..."} / string
    if (data && typeof data === 'object') {
      const msg = data.error || data.msg || data.message;
      return Promise.reject({ ...data, error: msg || data.error });
    }
    return Promise.reject({ error: data || error.message });
  }
);

export interface User {
  id: number;
  username: string;
  email: string;
  is_admin: boolean;
  is_active: boolean;
  auth_type: string;
}

interface LoginResponse {
  access_token: string;
  user: User;
}

interface UploadResponse {
  message: string;
  file: any;
}

export const authApi = {
  login: async (username: string, password: string, authType?: string) => {
    const res = await api.post<LoginResponse>('/auth/login', { username, password, auth_type: authType });
    return res.data;
  },
  register: async (username: string, email: string, password: string) => {
    const res = await api.post('/auth/register', { username, email, password });
    return res.data;
  },
  getCurrentUser: async () => {
    const res = await api.get<User>('/auth/me');
    return res.data;
  },
  checkUserType: async (username: string) => {
    const res = await api.post<{ exists: boolean; auth_type?: string }>('/auth/check-user', { username });
    return res.data;
  },
  changePassword: async (oldPassword: string, newPassword: string) => {
    const res = await api.post('/auth/change-password', { old_password: oldPassword, new_password: newPassword });
    return res.data;
  },
};

// 上传进度回调类型
export interface UploadProgress {
  loaded: number;       // 已上传字节
  total: number;        // 总字节
  percent: number;      // 百分比 0-100
  speed: number;        // 速度 bytes/s
  speedText: string;    // 格式化后的速度 如 "2.5 MB/s"
  remaining: number;    // 剩余时间(秒)
  remainingText: string; // 格式化剩余时间
}

// 下载进度回调类型
export interface DownloadProgress {
  loaded: number;
  total: number;
  percent: number;
  speed: number;
  speedText: string;
  remaining: number;
  remainingText: string;
}

// 格式化文件大小
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// 格式化速度
const formatSpeed = (bytesPerSec: number): string => {
  if (bytesPerSec === 0) return '0 B/s';
  const k = 1024;
  const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
  const i = Math.floor(Math.log(bytesPerSec) / Math.log(k));
  return parseFloat((bytesPerSec / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// 格式化剩余时间
const formatRemaining = (seconds: number): string => {
  if (!isFinite(seconds) || seconds <= 0) return '--';
  if (seconds < 60) return `${Math.round(seconds)}秒`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}分${Math.round(seconds % 60)}秒`;
  return `${Math.floor(seconds / 3600)}小时${Math.floor((seconds % 3600) / 60)}分`;
};

// 最大上传文件大小 (与nginx配置一致)
export const MAX_UPLOAD_SIZE = 50 * 1024 * 1024 * 1024; // 50GB
export const MAX_UPLOAD_SIZE_TEXT = '50 GB';

export const fileApi = {
  // 带进度的上传
  uploadWithProgress: (
    file: File,
    comment: string | undefined,
    onProgress: (progress: UploadProgress) => void,
    abortController?: AbortController,
    /** 浏览器已发完请求体后触发（服务器尚在处理，如病毒扫描）；用于切换 UI「正在扫描」 */
    onUploadBytesComplete?: () => void
  ): Promise<UploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    if (comment) formData.append('comment', comment);
    
    const startTime = Date.now();
    let lastLoaded = 0;
    let lastTime = startTime;
    let uploadCompleteNotified = false;
    
    return api
      .post<UploadResponse>('/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: abortController?.signal,
        onUploadProgress: (progressEvent) => {
          const loaded = progressEvent.loaded;
          const total = progressEvent.total || file.size;
          const percent = Math.round((loaded / total) * 100);
          
          const now = Date.now();
          const timeDiff = (now - lastTime) / 1000; // 秒
          
          // 计算瞬时速度（每200ms更新一次）
          let speed = 0;
          if (timeDiff >= 0.2) {
            speed = (loaded - lastLoaded) / timeDiff;
            lastLoaded = loaded;
            lastTime = now;
          } else {
            // 使用平均速度
            const totalTime = (now - startTime) / 1000;
            speed = totalTime > 0 ? loaded / totalTime : 0;
          }
          
          const remaining = speed > 0 ? (total - loaded) / speed : 0;
          
          onProgress({
            loaded,
            total,
            percent,
            speed,
            speedText: formatSpeed(speed),
            remaining,
            remainingText: formatRemaining(remaining),
          });

          const done = total > 0 && loaded >= total;
          if (!uploadCompleteNotified && done) {
            uploadCompleteNotified = true;
            // 下一帧再切到「扫描」，以便先渲染 100% 上传条
            setTimeout(() => onUploadBytesComplete?.(), 0);
          }
        },
      })
      .then((r) => r.data);
  },
  
  // 原有的简单上传（保持兼容）
  upload: (file: File, comment?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (comment) formData.append('comment', comment);
    return api
      .post<UploadResponse>('/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
  
  list: async (status?: string) => {
    const res = await api.get<any[]>('/files', { params: { status } });
    return res.data;
  },
  
  // 带进度的下载
  downloadWithProgress: async (
    fileId: number,
    onProgress: (progress: DownloadProgress) => void,
    abortController?: AbortController
  ): Promise<Blob> => {
    const startTime = Date.now();
    let lastLoaded = 0;
    let lastTime = startTime;
    
    const res = await api.get<Blob>(`/files/${fileId}/download`, {
      responseType: 'blob',
      signal: abortController?.signal,
      onDownloadProgress: (progressEvent) => {
        const loaded = progressEvent.loaded;
        const total = progressEvent.total || 0;
        const percent = total > 0 ? Math.round((loaded / total) * 100) : 0;
        
        const now = Date.now();
        const timeDiff = (now - lastTime) / 1000;
        
        let speed = 0;
        if (timeDiff >= 0.2) {
          speed = (loaded - lastLoaded) / timeDiff;
          lastLoaded = loaded;
          lastTime = now;
        } else {
          const totalTime = (now - startTime) / 1000;
          speed = totalTime > 0 ? loaded / totalTime : 0;
        }
        
        const remaining = speed > 0 && total > 0 ? (total - loaded) / speed : 0;
        
        onProgress({
          loaded,
          total,
          percent,
          speed,
          speedText: formatSpeed(speed),
          remaining,
          remainingText: formatRemaining(remaining),
        });
      },
    });
    return res.data;
  },
  
  // 原有的简单下载
  download: async (fileId: number) => {
    const res = await api.get<Blob>(`/files/${fileId}/download`, { responseType: 'blob' });
    return res.data;
  },
  
  review: async (fileId: number, action: 'approve' | 'reject', comment?: string) => {
    const res = await api.post(`/files/${fileId}/review`, { action, comment });
    return res.data;
  },
  remove: async (fileId: number) => {
    const res = await api.delete(`/files/${fileId}`);
    return res.data;
  },
  /** 用户中转已通过审核的文件 */
  forward: async (fileIds: number[], targetUserIds: number[]) => {
    const res = await api.post('/files/forward', {
      file_ids: fileIds,
      target_user_ids: targetUserIds,
    });
    return res.data;
  },
  /** 中转目标用户目录 */
  listUsersForForward: async () => {
    const res = await api.get<Pick<User, 'id' | 'username' | 'email' | 'auth_type'>[]>('/users');
    return res.data;
  },
};

export const statsApi = {
  getStats: async () => {
    const res = await api.get('/stats');
    return res.data;
  },
};

export const adminApi = {
  listUsers: async () => {
    const res = await api.get<User[]>('/admin/users');
    return res.data;
  },
  resetUserPassword: async (userId: number, newPassword: string) => {
    const res = await api.post(`/admin/users/${userId}/reset-password`, { new_password: newPassword });
    return res.data;
  },
  getUserFiles: async (userId: number) => {
    const res = await api.get(`/admin/users/${userId}/files`);
    return res.data;
  },
  forwardFiles: async (fileIds: number[], targetUserIds: number[]) => {
    const res = await api.post('/admin/files/forward', {
      file_ids: fileIds,
      target_user_ids: targetUserIds
    });
    return res.data;
  },
  deleteUser: async (userId: number) => {
    const res = await api.delete(`/admin/users/${userId}`);
    return res.data;
  },
  setUserAdmin: async (userId: number, isAdmin: boolean) => {
    const res = await api.patch(`/admin/users/${userId}/admin`, { is_admin: isAdmin });
    return res.data;
  },
  listNotificationRecipients: async () => {
    const res = await api.get<NotificationRecipient[]>('/admin/notification-recipients');
    return res.data;
  },
  addNotificationRecipient: async (email: string) => {
    const res = await api.post('/admin/notification-recipients', { email });
    return res.data;
  },
  deleteNotificationRecipient: async (recipientId: number) => {
    const res = await api.delete(`/admin/notification-recipients/${recipientId}`);
    return res.data;
  },
};

export interface NotificationRecipient {
  id: number;
  email: string;
  created_at?: string;
}

export interface AuditLogItem {
  id: number;
  file_id?: number;
  file_name?: string;
  user_id?: number;
  username?: string;
  action: string;
  ip?: string;
  transfer_direction?: string;
  detail?: string;
  created_at: string;
}

export interface AuditLogListResponse {
  items: AuditLogItem[];
  total: number;
  page: number;
  page_size: number;
}

export const auditApi = {
  list: async (page = 1, pageSize = 20, action?: string) => {
    const res = await api.get<AuditLogListResponse>('/admin/logs', {
      params: { page, page_size: pageSize, action },
    });
    return res.data;
  },
};

export default api;
